import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { market } from '../services/api';
import { Holding } from '../types';

interface Props {
  holdings: Holding[];
  onTrade?: (symbol: string, type: 'BUY' | 'SELL') => void;
  onOpenChart?: (symbol: string) => void;
}

interface NewsItem {
  id: string;
  symbol: string;
  title: string;
  source: string;
  url: string;
  publishedAt: string;
  publishedTimeAgo: string;
  sentiment: 'BULLISH' | 'NEUTRAL' | 'BEARISH';
  sentimentScore: number;
  summary: string;
  impact: 'HIGH' | 'MEDIUM' | 'LOW';
}

interface FearAndGreed {
  score: number;
  rating: string;
  timestamp: string;
}

export const LiveMarketSentimentHub: React.FC<Props> = ({ holdings, onTrade, onOpenChart }) => {
  const { language } = useLanguage();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [fng, setFng] = useState<FearAndGreed>({ score: 73, rating: 'Greed', timestamp: '' });
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filterSentiment, setFilterSentiment] = useState<'ALL' | 'BULLISH' | 'NEUTRAL' | 'BEARISH'>('ALL');
  const [selectedAssetSymbol, setSelectedAssetSymbol] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchLiveNews = useCallback(async () => {
    try {
      const res = await market.getLiveNews();
      if (res.data) {
        if (Array.isArray(res.data.news)) {
          setNews(res.data.news);
        }
        if (res.data.fearAndGreed) {
          setFng(res.data.fearAndGreed);
        }
      }
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch live market news', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveNews();
    const interval = setInterval(fetchLiveNews, 60000); // 1-minute auto poll
    return () => clearInterval(interval);
  }, [fetchLiveNews]);

  const handleManualRefresh = () => {
    setIsRefreshing(true);
    fetchLiveNews();
  };

  // Distinct list of holdings symbols
  const portfolioTickers = Array.from(new Set([
    ...holdings.map(h => h.symbol),
    'CSG', 'WDC', 'SNDK', 'LITE', 'MU', 'AAPL', 'NVDA'
  ])).filter(Boolean);

  // Filtered news
  const filteredNews = news.filter(item => {
    if (filterSentiment !== 'ALL' && item.sentiment !== filterSentiment) return false;
    if (selectedAssetSymbol !== 'ALL' && item.symbol !== selectedAssetSymbol) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        item.title.toLowerCase().includes(q) ||
        item.symbol.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.source.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Calculate sentiment metrics per symbol
  const getSymbolSentimentMeta = (sym: string) => {
    const symbolNews = news.filter(n => n.symbol === sym);
    if (symbolNews.length === 0) return { count: 0, score: 0, sentiment: 'NEUTRAL' };
    const avg = Math.round(symbolNews.reduce((sum, n) => sum + n.sentimentScore, 0) / symbolNews.length);
    return {
      count: symbolNews.length,
      score: avg,
      sentiment: avg > 15 ? 'BULLISH' : avg < -15 ? 'BEARISH' : 'NEUTRAL'
    };
  };

  // Active focused holding details (if specific asset selected)
  const activeHolding = holdings.find(h => h.symbol === selectedAssetSymbol);
  const activeMeta = selectedAssetSymbol !== 'ALL' ? getSymbolSentimentMeta(selectedAssetSymbol) : null;

  return (
    <div className="space-y-3 font-mono text-xs animate-[fadeIn_0.2s_ease-out]">
      {/* 1. SUBTLE STRIP: Fear & Greed + Live Source Indicators + Manual Refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-3.5 py-2.5 bg-white/90 backdrop-blur-md border border-slate-200/80 rounded-xl shadow-2xs">
        <div className="flex items-center gap-3.5 flex-wrap">
          {/* Subtle Fear & Greed Gauge */}
          <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
            <span className="text-[10px] uppercase font-black text-slate-400">Fear & Greed:</span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-black text-slate-900">{fng.score}</span>
              <span className={`px-1.5 py-0.2 rounded text-[9px] font-black uppercase border ${
                fng.score >= 60 ? 'bg-emerald-50 text-emerald-700 border-emerald-300' :
                fng.score <= 40 ? 'bg-rose-50 text-rose-700 border-rose-300' :
                'bg-amber-50 text-amber-700 border-amber-300'
              }`}>
                {fng.rating}
              </span>
            </div>
            {/* Micro progress line */}
            <div className="w-12 bg-slate-100 h-1.5 rounded-full overflow-hidden hidden sm:block">
              <div 
                className="bg-slate-900 h-full rounded-full transition-all duration-500" 
                style={{ width: `${Math.max(5, Math.min(100, fng.score))}%` }}
              ></div>
            </div>
          </div>

          {/* Active Aggregated Sources Pill */}
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="hidden md:inline text-slate-400 font-medium">Zdroje:</span>
            <div className="flex items-center gap-1 flex-wrap">
              <span className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 font-semibold text-[9px] border border-slate-200">Yahoo Live</span>
              <span className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 font-semibold text-[9px] border border-slate-200">CNBC</span>
              <span className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 font-semibold text-[9px] border border-slate-200">MarketWatch</span>
              <span className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 font-semibold text-[9px] border border-slate-200">WSJ</span>
              <span className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 font-semibold text-[9px] border border-slate-200">BBC</span>
              <span className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 font-semibold text-[9px] border border-slate-200">E15 / CSG</span>
            </div>
          </div>
        </div>

        {/* Sync Controls */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">
            ⏱️ {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 hover:text-slate-900 rounded-lg font-black text-[10px] transition-all flex items-center gap-1 border border-slate-200 disabled:opacity-50"
            title="Okamžite stiahnuť nové správy"
          >
            <span className={isRefreshing ? 'animate-spin inline-block' : ''}>🔄</span>
            <span>{isRefreshing ? 'Sťahujem...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* 2. TICKER SELECTOR BAR (Subtílna horizontálna lišta vašich akcií s počtom správ a AI skóre) */}
      <div className="bg-white px-3 py-2 rounded-xl border border-slate-200/80 shadow-2xs flex items-center gap-1.5 overflow-x-auto">
        <span className="text-[10px] font-black uppercase text-slate-400 px-1 shrink-0">
          Moje Akcie:
        </span>

        {/* Button All */}
        <button
          onClick={() => setSelectedAssetSymbol('ALL')}
          className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all shrink-0 border ${
            selectedAssetSymbol === 'ALL'
              ? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
              : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
          }`}
        >
          🌐 Všetko ({news.length})
        </button>

        {/* Dynamic Buttons for Portfolio Tickers */}
        {portfolioTickers.map(sym => {
          const meta = getSymbolSentimentMeta(sym);
          const isSelected = selectedAssetSymbol === sym;

          return (
            <button
              key={sym}
              onClick={() => setSelectedAssetSymbol(sym)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all shrink-0 flex items-center gap-1.5 border ${
                isSelected
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-2xs'
                  : 'bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100'
              }`}
            >
              <span>{sym}</span>
              {meta.count > 0 && (
                <span className={`px-1 py-0.2 rounded text-[9px] font-black ${
                  isSelected 
                    ? 'bg-white/25 text-white' 
                    : meta.sentiment === 'BULLISH' 
                    ? 'bg-emerald-100 text-emerald-800' 
                    : meta.sentiment === 'BEARISH' 
                    ? 'bg-rose-100 text-rose-800' 
                    : 'bg-slate-200 text-slate-700'
                }`}>
                  {meta.count} {meta.score !== 0 ? `(${meta.score > 0 ? '+' : ''}${meta.score}%)` : ''}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 3. DEDICATED ASSET CONTEXT CARD (If a specific holding is selected) */}
      {selectedAssetSymbol !== 'ALL' && (
        <div className="bg-slate-900 text-white p-3.5 rounded-xl border border-slate-800 flex flex-wrap items-center justify-between gap-3 animate-[fadeIn_0.15s_ease-out]">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center font-black text-xs text-white border border-white/10">
              {selectedAssetSymbol}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-black text-xs text-white">{selectedAssetSymbol}</span>
                {activeHolding && (
                  <span className="text-[10px] text-slate-400">
                    {activeHolding.name} • {activeHolding.quantity} ks v portfóliu
                  </span>
                )}
                {activeMeta && activeMeta.count > 0 && (
                  <span className={`px-1.5 py-0.2 rounded text-[9px] font-black border ${
                    activeMeta.sentiment === 'BULLISH' ? 'bg-emerald-950 text-emerald-400 border-emerald-800' :
                    activeMeta.sentiment === 'BEARISH' ? 'bg-rose-950 text-rose-400 border-rose-800' :
                    'bg-slate-800 text-slate-300 border-slate-700'
                  }`}>
                    AI SENTIMENT: {activeMeta.score > 0 ? '+' : ''}{activeMeta.score}% ({activeMeta.sentiment})
                  </span>
                )}
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Vyfiltrovaný tok z viacerých zdrojov (Yahoo, Dow Jones, Reuters, E15) pre <strong>{selectedAssetSymbol}</strong>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onTrade && (
              <button
                onClick={() => onTrade(selectedAssetSymbol, 'BUY')}
                className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[10px] transition-all"
              >
                + Kúpiť {selectedAssetSymbol}
              </button>
            )}
            {onOpenChart && (
              <button
                onClick={() => onOpenChart(selectedAssetSymbol)}
                className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] transition-all"
              >
                📈 Otvoriť Graf
              </button>
            )}
            <button
              onClick={() => setSelectedAssetSymbol('ALL')}
              className="px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white font-bold text-[10px]"
            >
              ✕ Zrušiť filter
            </button>
          </div>
        </div>
      )}

      {/* 4. SUBTLE COMPACT FILTER BAR */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 px-1">
        {/* Sentiment Pill Filters */}
        <div className="flex items-center gap-1 flex-wrap">
          {(['ALL', 'BULLISH', 'NEUTRAL', 'BEARISH'] as const).map(s => (
            <button
              key={s}
              onClick={() => setFilterSentiment(s)}
              className={`px-2 py-0.8 rounded-lg text-[10px] font-black border transition-all ${
                filterSentiment === s
                  ? s === 'BULLISH' ? 'bg-emerald-600 text-white border-emerald-600'
                  : s === 'BEARISH' ? 'bg-rose-600 text-white border-rose-600'
                  : 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {s === 'ALL' ? 'Všetok sentiment' :
               s === 'BULLISH' ? '🟢 Býčí' :
               s === 'BEARISH' ? '🔴 Medvedí' :
               '🟡 Neutrál'}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-56">
          <input
            type="text"
            placeholder="Hľadať správu, titul alebo zdroj..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-lg pl-6 pr-6 py-0.8 text-[11px] outline-none focus:border-indigo-600 font-mono shadow-2xs"
          />
          <span className="absolute left-2 top-1.5 text-slate-400 text-[10px] pointer-events-none">🔍</span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1 text-slate-400 hover:text-slate-800 font-bold px-1"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* 5. SUBTLE STREAMLINED NEWS FEED (Kompaktný čistý inštitucionálny zoznam) */}
      <div className="space-y-1.5">
        {loading ? (
          <div className="py-10 text-center text-slate-400 font-bold border border-dashed border-slate-200 rounded-xl space-y-1">
            <span className="text-xl animate-spin inline-block">🔄</span>
            <p className="text-[11px]">Sťahujem živé správy z viacerých trhových zdrojov...</p>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="py-10 text-center text-slate-400 font-bold border border-dashed border-slate-200 rounded-xl space-y-1">
            <span className="text-xl block">📰</span>
            <p className="text-[11px]">Žiadne správy pre zvolené kritériá.</p>
          </div>
        ) : (
          filteredNews.map(item => {
            const isBullish = item.sentiment === 'BULLISH';
            const isBearish = item.sentiment === 'BEARISH';

            return (
              <div
                key={item.id}
                className="p-2.5 bg-white hover:bg-slate-50 border border-slate-200/70 hover:border-slate-300 rounded-xl transition-all shadow-2xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 group"
              >
                {/* Left: Ticker Badge, Source, Title & Summary */}
                <div className="space-y-0.5 flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-[9px]">
                    {/* Ticker Chip */}
                    <button
                      onClick={() => setSelectedAssetSymbol(item.symbol)}
                      className={`px-1.5 py-0.2 rounded font-black uppercase border transition-colors ${
                        item.symbol !== 'MARKET'
                          ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                          : 'bg-slate-100 text-slate-600 border-slate-200'
                      }`}
                    >
                      {item.symbol}
                    </button>

                    <span className="text-slate-500 font-bold">{item.source}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-slate-400">⏱️ {item.publishedTimeAgo}</span>
                  </div>

                  {/* Headline Title */}
                  <h4 className="font-bold text-slate-900 text-[11px] leading-snug group-hover:text-indigo-600 transition-colors">
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {item.title}
                    </a>
                  </h4>

                  {/* One-line summary */}
                  {item.summary && (
                    <p className="text-slate-500 text-[10px] leading-tight truncate max-w-4xl font-sans">
                      {item.summary}
                    </p>
                  )}
                </div>

                {/* Right: AI Sentiment Pill & External Link */}
                <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                  <span className={`px-1.5 py-0.5 rounded text-[8.5px] font-black border uppercase tracking-wider ${
                    isBullish
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                      : isBearish
                      ? 'bg-rose-50 text-rose-800 border-rose-300'
                      : 'bg-slate-50 text-slate-600 border-slate-200'
                  }`}>
                    {isBullish ? `+${item.sentimentScore}% BÝČÍ` : isBearish ? `${item.sentimentScore}% MEDVEDÍ` : 'NEUTRÁL'}
                  </span>

                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors font-bold text-xs"
                    title="Otvoriť originálny článok"
                  >
                    ↗
                  </a>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
